import { AnalyticsEventName } from 'api/analytics';
import type { SearchAndFilterProps } from 'components/SearchAndFilter/helpers';
import { useAnalytics } from 'helpers/analytics';
import { useTheme } from 'hooks/theme';
import { useIsGamePage } from 'hooks/useIsGamePage';
import { useMedia } from 'modules/common/hooks/useMedia';
import { sendSearchOverallAppearanceEvent } from 'modules/googleAnalytics/events/searchGamesEvents';
import type { ButtonHTMLAttributes, FC } from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './Button';
import type {
  ScButtonCrossProps,
  ScButtonMoreProps,
  ScSearchButtonProps,
} from './styled';
import {
  ScButtonCross,
  ScButtonMore,
  ScFilterButton,
  ScSearchButton,
} from './styled';

export { Button } from './Button';

interface ButtonCrossProps extends ScButtonCrossProps {
  className?: string;
  onClick?: (e: any) => void;
  inCircle?: boolean;
  small?: boolean;
}

export const ButtonCross: FC<ButtonCrossProps> = ({
  inCircle,
  small,
  ...props
}) => {
  const {
    crossIcon2: CrossIcon,
    crossIcon3: CrossIconCircle,
    crossSmallIcon: CrossSmallIcon,
  } = useTheme();

  return (
    <ScButtonCross inCircle={inCircle} small={small} {...props}>
      {inCircle ? (
        <CrossIconCircle />
      ) : small ? (
        <CrossSmallIcon />
      ) : (
        <CrossIcon />
      )}
    </ScButtonCross>
  );
};

export const ButtonMore: FC<
  ScButtonMoreProps & ButtonHTMLAttributes<HTMLButtonElement>
> = (props) => (
  <ScButtonMore {...props}>
    <div />
    <div />
    <div />
  </ScButtonMore>
);

interface SearchButtonProps {
  light?: boolean;
  isABVersion?: boolean;
}

export const SearchButton: FC<SearchButtonProps & ScSearchButtonProps> = ({
  light,
  isABVersion,
  ...props
}) => {
  const { t } = useTranslation(['glossary', 'common']);
  const { search2Icon: SearchIcon } = useTheme();
  const { sendEventToAnalytics } = useAnalytics();

  const { isGamePage } = useIsGamePage();

  const { isMobileS } = useMedia();

  const buttonMode = useMemo(
    () => (light ? { lightGray: true } : { gray: true }),
    [light]
  );

  const handleButtonClick = useCallback(() => {
    const searchOverallEventType = isGamePage ? 'game_page' : 'search';

    sendSearchOverallAppearanceEvent(searchOverallEventType);

    sendEventToAnalytics({
      name: AnalyticsEventName.LOBBY__SEARCH__INPUT__CLICK,
    });
  }, [isGamePage, sendEventToAnalytics]);

  return (
    <ScSearchButton {...props} isABVersion={isABVersion}>
      <Button
        to="/search-games"
        wide={isMobileS}
        xLarge
        {...buttonMode}
        onClick={handleButtonClick}
      >
        <SearchIcon />
        {isABVersion ? (
          <div className="content">{t('common:search.title')}</div>
        ) : (
          <div className="content">
            <div className="step-1">
              <span className="word">{t('glossary:games')}, </span>
              <span className="word">{t('glossary:providers')}, </span>
              <span className="word">{t('glossary:themes')}, </span>
              <span className="word">{t('glossary:categories')}</span>
            </div>
            <div className="step-2">
              <span className="word">{t('glossary:or')}</span>
            </div>
            <div className="step-3">
              <span className="word">{t('glossary:searchButton.word1')} </span>
              <span className="word">{t('glossary:searchButton.word2')} </span>
              <span className="word">{t('glossary:searchButton.word3')} </span>
              <span className="word">{t('glossary:searchButton.word4')} </span>
              <span className="word">{t('glossary:searchButton.word5')} </span>
              <span className="word">{t('glossary:searchButton.word6')}</span>
            </div>
          </div>
        )}
      </Button>
    </ScSearchButton>
  );
};

type FilterButtonProsp = Pick<SearchAndFilterProps, 'eventName'> & {
  light?: boolean;
};

export const FilterButton: FC<FilterButtonProsp> = ({ eventName, light }) => {
  const { t } = useTranslation('glossary');
  const { filterIcon: FilterIcon } = useTheme();
  const { sendEventToAnalytics } = useAnalytics();
  const { isGamePage } = useIsGamePage();

  const { isMobileS } = useMedia();

  const handleButtonClick = useCallback(() => {
    const searchOverallEventType = isGamePage ? 'game_page' : 'filter';

    sendSearchOverallAppearanceEvent(searchOverallEventType);
    sendEventToAnalytics({
      name: eventName,
    });
  }, [eventName, isGamePage, sendEventToAnalytics]);

  const buttonMode = useMemo(
    () => (light ? { lightGray: true } : { gray: true }),
    [light]
  );

  return (
    <ScFilterButton>
      <Button
        to="/search-games#categories-slider"
        wide={isMobileS}
        xLarge
        {...buttonMode}
        onClick={handleButtonClick}
      >
        <FilterIcon />
        {!isMobileS && <div className="content">{t('filterButton')}</div>}
      </Button>
    </ScFilterButton>
  );
};
